/**
 * SystemHealthView — admin page listing configuration and integration status.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import { ReloadOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { getSystemHealth } from "../services/systemHealthService";

const { Title, Text } = Typography;

const Page = styled.div`
  padding: 24px;
  max-width: 1100px;
  margin: 0 auto;
`;

const statusColor = (status) => {
  switch (status) {
    case "working":
    case "healthy":
      return "success";
    case "degraded":
    case "partial":
      return "warning";
    case "failed":
      return "error";
    default:
      return "default";
  }
};

const overallLabel = (overall) => {
  switch (overall) {
    case "healthy":
      return "All checks passing";
    case "degraded":
      return "Some checks degraded";
    case "failed":
      return "Critical failures detected";
    default:
      return overall;
  }
};

export default function SystemHealthView() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [health, setHealth] = useState(null);

  const load = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);
      const result = await getSystemHealth();
      if (!result.success) {
        throw new Error(result.error || "Failed to load system health");
      }
      setHealth(result.data);
    } catch (err) {
      setError(err.message || "Failed to load system health");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  const columns = [
    {
      title: "Check",
      dataIndex: "name",
      key: "name",
      render: (name, row) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.category}
          </Text>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color={statusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: "Configured",
      dataIndex: "configured",
      key: "configured",
      width: 110,
      render: (configured) => (
        <Tag color={configured ? "blue" : "default"}>
          {configured ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Details",
      dataIndex: "message",
      key: "message",
      render: (message) => (
        <Text style={{ whiteSpace: "pre-wrap" }}>{message}</Text>
      ),
    },
  ];

  if (loading) {
    return (
      <Page style={{ textAlign: "center", paddingTop: 80 }}>
        <Spin size="large" />
      </Page>
    );
  }

  return (
    <Page>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Link to="/settings">
            <Button type="link" icon={<ArrowLeftOutlined />} style={{ paddingLeft: 0 }}>
              Back to settings
            </Button>
          </Link>
          <Title level={3} style={{ marginTop: 8, marginBottom: 4 }}>
            System health
          </Title>
          <Text type="secondary">
            Configuration and integration checks — secrets are never shown, only
            whether each service is configured and working.
          </Text>
        </div>

        {error && (
          <Alert type="error" message={error} showIcon closable onClose={() => setError(null)} />
        )}

        {health && (
          <>
            <Alert
              type={statusColor(health.overall)}
              showIcon
              message={
                <Space>
                  <span>{overallLabel(health.overall)}</span>
                  <Tag>{health.environment}</Tag>
                </Space>
              }
              description={`Last checked ${new Date(health.checkedAt).toLocaleString()}`}
              action={
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  loading={refreshing}
                  onClick={() => load(true)}
                >
                  Refresh
                </Button>
              }
            />

            <Card>
              <Table
                rowKey="id"
                columns={columns}
                dataSource={health.checks || []}
                pagination={false}
                size="middle"
              />
            </Card>
          </>
        )}
      </Space>
    </Page>
  );
}
